import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, interval, Subscription } from 'rxjs';
import { RunView, LogError, UserInfo } from '@memberjunction/core';
import { AnnouncementsEntity, AnnouncementUserStatesEntity } from 'mj_generatedentities';
import { UserService } from './user.service';
import { NotificationService } from './notification.service';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class AnnouncementService implements OnDestroy {
  private unreadCountSubject = new BehaviorSubject<number>(0);
  private announcementsSubject = new BehaviorSubject<AnnouncementsEntity[]>([]);
  private pollingSubscription?: Subscription;
  private currentUser?: UserInfo;
  
  // Observables for components to subscribe to
  unreadCount$ = this.unreadCountSubject.asObservable();
  announcements$ = this.announcementsSubject.asObservable();
  
  // Configuration from environment
  private readonly POLLING_ENABLED = environment.ANNOUNCEMENT_POLLING?.enabled ?? false;
  private readonly POLLING_INTERVAL = (environment.ANNOUNCEMENT_POLLING?.intervalSeconds ?? 30) * 1000; // Convert to milliseconds
  private readonly URGENT_NOTIFICATION_DURATION = environment.ANNOUNCEMENT_POLLING?.urgentNotificationDuration ?? 8000;
  private readonly HIGH_NOTIFICATION_DURATION = environment.ANNOUNCEMENT_POLLING?.highNotificationDuration ?? 5000;
  
  constructor(
    private userService: UserService,
    private notificationService: NotificationService
  ) {
    this.initializeService();
  }

  ngOnDestroy(): void {
    this.stopPolling();
  }

  /**
   * Initialize the service and conditionally start real-time updates
   */
  private initializeService(): void {
    // Get current user and load initial data
    this.currentUser = this.userService.getUserInfo();
    if (this.currentUser) {
      this.loadInitialData();
      
      // Only start polling if enabled in configuration
      if (this.POLLING_ENABLED) {
        this.startPolling();
      }
    }
  }

  /**
   * Load initial unread count and announcements
   */
  private async loadInitialData(): Promise<void> {
    try {
      await Promise.all([
        this.updateUnreadCount(),
        this.loadRecentAnnouncements()
      ]);
    } catch (error) {
      LogError('Failed to load initial announcement data:', undefined, error);
    }
  }

  /**
   * Start polling for updates (only if enabled in configuration)
   */
  private startPolling(): void {
    if (!this.POLLING_ENABLED) {
      return;
    }
    
    this.stopPolling(); // Ensure no existing polling
    
    // Poll at configured interval for updates
    this.pollingSubscription = interval(this.POLLING_INTERVAL).subscribe(() => {
      this.checkForUpdates();
    });
  }

  /**
   * Stop polling
   */
  private stopPolling(): void {
    if (this.pollingSubscription) {
      this.pollingSubscription.unsubscribe();
      this.pollingSubscription = undefined;
    }
  }

  /**
   * Check for updates and handle new announcements
   */
  private async checkForUpdates(): Promise<void> {
    try {
      const previousCount = this.unreadCountSubject.value;
      await this.updateUnreadCount();
      
      const newCount = this.unreadCountSubject.value;
      
      // If count increased, check for new announcements
      if (newCount > previousCount) {
        await this.handleNewAnnouncements();
      }
    } catch (error) {
      LogError('Failed to check for announcement updates:', undefined, error);
    }
  }

  /**
   * Update the unread count using parallel queries for better performance
   */
  private async updateUnreadCount(): Promise<void> {
    if (!this.currentUser) return;

    try {
      const rv = new RunView();
      
      // Get both datasets in parallel for better performance
      const [announcementsResult, userStatesResult] = await rv.RunViews([
        {
          EntityName: 'Announcements',
          ExtraFilter: `Status = 'published' AND (ExpiryDate IS NULL OR ExpiryDate > GETUTCDATE())`,
          ResultType: 'entity_object'
        },
        {
          EntityName: 'Announcement User States',
          ExtraFilter: `UserID = '${this.currentUser.ID}' AND Status IN ('read', 'dismissed')`,
          ResultType: 'entity_object'
        }
      ]);

      if (announcementsResult.Success) {
        const allAnnouncements = announcementsResult.Results as AnnouncementsEntity[];
        const readAnnouncementIds = userStatesResult.Success ? 
          (userStatesResult.Results as AnnouncementUserStatesEntity[]).map(s => s.AnnouncementID) : [];
        
        // Filter out announcements that user has already read/dismissed
        const unreadAnnouncements = allAnnouncements.filter(a => !readAnnouncementIds.includes(a.ID));
        
        
        const unreadCount = unreadAnnouncements.length;
        this.unreadCountSubject.next(unreadCount);
      } else {
        throw new Error(announcementsResult.ErrorMessage);
      }
    } catch (error) {
      LogError('Failed to update unread count:', undefined, error);
    }
  }

  /**
   * Load recent announcements for notifications
   */
  private async loadRecentAnnouncements(): Promise<void> {
    if (!this.currentUser) return;

    try {
      const rv = new RunView();
      
      const result = await rv.RunView({
        EntityName: 'Announcements',
        ExtraFilter: `
          Status = 'published' 
          AND (ExpiryDate IS NULL OR ExpiryDate > GETUTCDATE())
          AND PublishDate > DATEADD(hour, -1, GETUTCDATE())
        `,
        OrderBy: 'PublishDate DESC',
        ResultType: 'entity_object'
      });

      if (result.Success) {
        const announcements = result.Results as AnnouncementsEntity[];
        this.announcementsSubject.next(announcements);
      }
    } catch (error) {
      LogError('Failed to load recent announcements:', undefined, error);
    }
  }

  /**
   * Handle new announcements by showing toast notifications
   */
  private async handleNewAnnouncements(): Promise<void> {
    try {
      await this.loadRecentAnnouncements();
      const recentAnnouncements = this.announcementsSubject.value;
      
      // Show notifications for high/urgent priority announcements
      recentAnnouncements.forEach(announcement => {
        this.showAnnouncementNotification(announcement);
      });
    } catch (error) {
      LogError('Failed to handle new announcements:', undefined, error);
    }
  }

  /**
   * Show toast notification for new announcement
   */
  private showAnnouncementNotification(announcement: AnnouncementsEntity): void {
    const priority = announcement.Priority?.toLowerCase();
    
    if (priority === 'urgent' || priority === 'high') {
      const duration = priority === 'urgent' ? 
        this.URGENT_NOTIFICATION_DURATION : 
        this.HIGH_NOTIFICATION_DURATION;
      
      const priorityIcon = this.getPriorityIcon(priority);
      
      this.notificationService.info(
        `${priorityIcon} New ${priority.toUpperCase()} Announcement`,
        announcement.Title,
        duration
      );
    }
  }

  /**
   * Get priority icon for notifications
   */
  private getPriorityIcon(priority: string): string {
    const icons: Record<string, string> = {
      'low': '🔵',
      'normal': '🟢',
      'high': '🟡',
      'urgent': '🔴'
    };
    return icons[priority] || '🟢';
  }

  /**
   * Mark announcement as read
   */
  async markAsRead(announcementId: string): Promise<void> {
    if (!this.currentUser) return;

    try {
      const rv = new RunView();
      
      // Check if user state already exists
      const stateResult = await rv.RunView({
        EntityName: 'Announcement User States',
        ExtraFilter: `UserID = '${this.currentUser.ID}' AND AnnouncementID = '${announcementId}'`,
        ResultType: 'entity_object'
      });

      let userState: AnnouncementUserStatesEntity;
      
      if (stateResult.Success && stateResult.Results.length > 0) {
        userState = stateResult.Results[0] as AnnouncementUserStatesEntity;
      } else {
        // Create new user state
        const md = await import('@memberjunction/core').then(m => new m.Metadata());
        userState = await md.GetEntityObject<AnnouncementUserStatesEntity>('Announcement User States');
        userState.NewRecord();
        userState.AnnouncementID = announcementId;
        userState.UserID = this.currentUser.ID;
      }

      // Update status to read
      userState.Status = 'read';
      userState.ReadAt = new Date();
      await userState.Save();

      // Update unread count
      await this.updateUnreadCount();
      
    } catch (error) {
      LogError('Failed to mark announcement as read:', undefined, error);
    }
  }

  /**
   * Manually refresh data (called by components)
   */
  async refresh(): Promise<void> {
    await this.loadInitialData();
  }

  /**
   * Get current unread count (synchronous)
   */
  getCurrentUnreadCount(): number {
    return this.unreadCountSubject.value;
  }

  /**
   * Update user context (called when user changes/impersonation)
   */
  updateUserContext(newUser: UserInfo): void {
    this.currentUser = newUser;
    this.loadInitialData();
    
    // Restart polling if enabled for the new user context
    if (this.POLLING_ENABLED) {
      this.startPolling();
    }
  }

}